import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiArrowRight, 
  FiCheckCircle,
  FiClock,
  FiUsers,
  FiBookOpen
} from 'react-icons/fi';
import { useContent } from '../context/ContentContext';
import './ProgramsPage.css';

const ProgramsPage = () => {
  const { content } = useContent();

  // Varsayılan programlar
  const defaultPrograms = [
    {
      id: 'lgs',
      title: 'LGS Hazırlık',
      subtitle: '8. Sınıf Öğrencileri İçin',
      description: 'Liselere Geçiş Sınavına (LGS) yönelik kapsamlı hazırlık programımızla öğrencilerimizi hayallerindeki liselere yerleştiriyoruz.',
      features: [
        'Tüm derslerden kapsamlı konu anlatımı',
        'Haftalık deneme sınavları',
        'Bireysel eksik analizi ve telafi',
        'Soru çözüm atölyeleri',
        'Online takip sistemi'
      ],
      duration: '9 Ay',
      classSize: 'Max 15 Kişi',
      color: '#667eea'
    },
    {
      id: 'yks',
      title: 'YKS-TYT-AYT Hazırlık',
      subtitle: 'Üniversite Adayları İçin',
      description: 'Yükseköğretim Kurumları Sınavına hazırlık programımızla öğrencilerimizi üniversite hayallerine kavuşturuyoruz.',
      features: [
        'TYT ve AYT tam hazırlık',
        'Alan bazlı uzman öğretmenler',
        'Gerçek sınav formatında denemeler',
        'Birebir koçluk sistemi',
        'Motivasyon ve stres yönetimi seminerleri'
      ],
      duration: '10 Ay',
      classSize: 'Max 12 Kişi',
      color: '#f093fb'
    },
    {
      id: 'dil',
      title: 'Yabancı Dil Kursları',
      subtitle: 'İngilizce & Almanca',
      description: 'Modern dil öğretim metodlarıyla İngilizce ve Almanca eğitimi veriyoruz. Tüm seviyelere uygun programlarımız mevcuttur.',
      features: [
        'Native speaker eğitmenler',
        'Konuşma kulüpleri',
        'Cambridge ve Goethe sertifika hazırlık',
        'Interaktif dersler',
        'Speaking ve listening odaklı eğitim'
      ],
      duration: 'Seviyeye Göre',
      classSize: 'Max 10 Kişi',
      color: '#4facfe'
    },
    {
      id: 'kodlama',
      title: 'Kodlama & Robotik',
      subtitle: '7-16 Yaş Arası',
      description: 'Geleceğin teknolojilerini bugünden öğretiyoruz. Çocuklarınız için eğlenceli ve öğretici kodlama dersleri.',
      features: [
        'Scratch ile temel programlama',
        'Python ve web geliştirme',
        'Robotik ve Arduino projeleri',
        'Oyun geliştirme atölyeleri',
        'Proje bazlı öğrenme'
      ],
      duration: '6-12 Ay',
      classSize: 'Max 8 Kişi',
      color: '#43e97b'
    },
    {
      id: 'etut',
      title: 'Etüt & Ödev Takip',
      subtitle: 'Tüm Sınıf Seviyeleri',
      description: 'Okul derslerine paralel etüt ve ödev takip programımızla öğrencilerimizin akademik başarısını destekliyoruz.',
      features: [
        'Günlük ödev kontrolü',
        'Okul konularına paralel çalışma',
        'Eksik konu telafisi',
        'Sınav hazırlığı',
        'Haftalık veli raporları'
      ],
      duration: 'Akademik Yıl',
      classSize: 'Max 12 Kişi',
      color: '#fa709a'
    },
    {
      id: 'ozel',
      title: 'Özel Ders',
      subtitle: 'Birebir Eğitim',
      description: 'İhtiyaçlarınıza özel birebir ders programları ile hedefe odaklı eğitim alın.',
      features: [
        'Kişiye özel müfredat',
        'Esnek ders saatleri',
        'Tüm derslerden destek',
        'Hızlı sonuç odaklı',
        'Detaylı performans takibi'
      ],
      duration: 'İsteğe Bağlı',
      classSize: '1 Kişi',
      color: '#a18cd1'
    }
  ];

  // Content'ten veya varsayılanlardan programları al
  const rawPrograms = content.services?.programs || content.services?.items || defaultPrograms;
  
  // Programları dönüştür (gradient yoksa color'dan oluştur)
  const programs = rawPrograms.map((p, index) => ({
    ...p,
    id: p.id || `program-${index}`,
    subtitle: p.subtitle || '',
    features: p.features || [],
    duration: p.duration || '',
    classSize: p.classSize || '',
    gradient: p.gradient || `linear-gradient(135deg, ${p.color || '#667eea'} 0%, ${adjustColor(p.color || '#667eea', 40)} 100%)`
  }));

  // Renk ayarlama fonksiyonu
  function adjustColor(hex, amount) {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
    return '#' + (0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1);
  }

  return (
    <div className="programs-page">
      {/* Hero */}
      <section className="programs-hero">
        <div className="programs-hero-bg"></div>
        <div className="container">
          <motion.div 
            className="programs-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>{content.services?.title || 'Eğitim Programlarımız'}</h1>
            <p>{content.services?.subtitle || 'Her seviyeye ve hedefe uygun kapsamlı eğitim programları'}</p>
          </motion.div>
        </div>
      </section>

      {/* Programs List */}
      <section className="programs-list section">
        <div className="container">
          {programs.map((program, index) => (
            <motion.div 
              key={program.id}
              className={`program-detail ${index % 2 === 1 ? 'reverse' : ''}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="program-detail-content">
                <div className="program-badge" style={{ background: program.gradient }}>
                  {program.subtitle}
                </div>
                <h2>{program.title}</h2>
                <p className="program-desc">{program.description}</p>
                
                <ul className="program-features">
                  {program.features.map((feature, i) => (
                    <li key={i}>
                      <FiCheckCircle />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="program-meta">
                  <div className="meta-item">
                    <FiClock />
                    <span>{program.duration}</span>
                  </div>
                  <div className="meta-item">
                    <FiUsers />
                    <span>{program.classSize}</span>
                  </div>
                </div>

                <Link to="/iletisim" className="btn btn-primary">
                  Kayıt İçin Başvur <FiArrowRight />
                </Link>
              </div>

              <div className="program-detail-image">
                <div className="program-image-wrapper" style={{ background: program.gradient }}>
                  <FiBookOpen />
                  <span>{program.title}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="programs-cta section section-dark">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>{content.cta?.programsTitle || 'Sizin İçin En Uygun Programı Belirleyelim'}</h2>
            <p>{content.cta?.programsDescription || 'Ücretsiz danışmanlık hizmetimizden yararlanın, çocuğunuzun ihtiyaçlarına en uygun programı birlikte seçelim.'}</p>
            <div className="cta-buttons">
              <Link to="/iletisim" className="btn btn-primary btn-lg">
                {content.cta?.programsPrimaryButton || 'Ücretsiz Danışmanlık'} <FiArrowRight />
              </Link>
              <a href={`tel:${content.contact?.phone || '02121234567'}`} className="btn btn-outline btn-lg">
                {content.cta?.programsSecondaryButton || 'Bizi Arayın'}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProgramsPage;

